// @ts-strict-ignore
import {ConfigOptions, ParamComponent} from 'panel';
import debounce from 'lodash/debounce';
import clamp from 'lodash/clamp';
import ResizeObserver from 'resize-observer-polyfill';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {rafRetry} from 'common/util/dom';
import {ToReactProps, wc2react} from 'common/util/react';
import mpFonts from 'common/stylesheets/mixins/fonts.json';
import {commaizeNumber, MeasureUtil} from 'common/util';
import {withStylesPanel} from 'common/util/panel';

import {
  AutocompleteType,
  InputChangeEvent,
  InputClipboardEvent,
  InputFocusEvent,
  InputInputEvent,
  InputKeyboardEvent,
  InputParams,
  inputParamsConfig,
  InputPointerEvent,
  InputSize,
  InputState,
  InputTheme,
  InputType,
  Necessary,
  TextAlign,
  ValidationStatus,
} from './types';
import {Template as template} from './template';
import css from './index.module.styl?inline';
import './label';

// Browser compatibility jank. See comment on `nextBlurShouldTriggerChange`.
const isSafariBrowser = navigator.userAgent.includes(`Safari`) && !navigator.userAgent.includes(`Chrome`);

interface InputJsxProps {
  // Uncomment to verify no more attrs are being passed
  // attrs?: void;
  props?: {
    value?: string;
  };
  params?: InputParams;
}

export class InputComponent extends ParamComponent<InputParams, InputState> {
  declare searchInput: HTMLInputElement;
  declare inputWrapperEl: HTMLElement;
  declare inputPrefixEl: HTMLElement;
  declare prefixResizeObserver: ResizeObserver;

  // TODO: This is janky. Figure out a way to get rid of it.
  debouncedChange = debounce((event: InputChangeEvent) => {
    this.params.onChange?.(this.state.value, event);
  }, 1000);

  get config(): ConfigOptions<InputState, unknown, unknown, InputParams> {
    return {
      params: inputParamsConfig,

      defaultParams: {
        autocomplete: AutocompleteType.Off,
        icon: ``,
        label: ``,
        type: InputType.Text,
        size: InputSize.Large,
        necessary: Necessary.Default,
        validationStatus: ValidationStatus.Default,
        // maxlength: Necessary.Optional,
        textAlign: TextAlign.Start,
        theme: InputTheme.Default,
      },

      template,

      defaultState: {
        nextBlurShouldTriggerChange: false,
        textWidth: 0,
        value: ``,
      },

      hooks: {
        preUpdate: (_newState, newParams?: InputParams) => {
          // Focus element if auto-focus attribute is added
          if (newParams && newParams.autoFocus && newParams.autoFocus !== this.params.autoFocus) {
            this.focus();
          } else if (newParams && newParams.placeholder !== this.params.placeholder) {
            this.updateInputWidth();
          }
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.params.autoFocus) {
      this.focus();
    }
    if (this.params.placeholder) {
      this.updateInputWidth();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.prefixResizeObserver?.disconnect();
  }

  supportsMinMaxAttrs() {
    return this.params.type === InputType.Number;
  }

  handleInput(ev: InputInputEvent) {
    const value = ev.target.value;
    this.update({nextBlurShouldTriggerChange: true});
    const updatedValue = this.changeValue(value);
    this.params.onInput?.(updatedValue, ev);

    if (!this.params.selectOnFocus) {
      // changeValue causes focus loss in some edge cases.
      // call focus() to maintain the focus in the dialogue while the user is actively typing
      //
      // don't call focus if `select-on-focus` is set to true because it will result in the
      // text being overriden
      this.focus();
    }
  }

  handleKeydown(ev: InputKeyboardEvent) {
    this.params.onKeydown?.(ev);
  }

  handleKeyup(ev: InputKeyboardEvent) {
    this.params.onKeyup?.(ev);
  }

  handlePaste(ev: InputClipboardEvent) {
    this.params.onPaste?.(ev);
  }

  handleChange(ev: InputChangeEvent) {
    ev.stopPropagation();
    let value = ev.target.value;
    if (this.params.type === InputType.Number) {
      let [min, max] = [Number.parseFloat(this.params.min), Number.parseFloat(this.params.max)];
      if (Number.isNaN(min)) {
        min = -Number.MAX_VALUE;
      }
      if (Number.isNaN(max)) {
        max = Number.MAX_VALUE;
      }
      // First remove commas
      value = this.parseCommifiedNumber(value);
      // Then clamp
      value = String(clamp(Number.parseFloat(value), min, max));
      // need to update the internal state value here. otherwise the prop and state is out of sync
      this.changeValue(value);
    }
    this.params.onChange?.(value, ev);
    // Note: Do NOT blur the input here; if a widget needs to blur-on-change,
    // it should be done in the widget itself as a special case to avoid
    // breaking browser-standard behavior by default.
  }

  handleBlur(ev: InputFocusEvent) {
    ev.stopPropagation();
    if (isSafariBrowser && this.state.nextBlurShouldTriggerChange) {
      this.update({nextBlurShouldTriggerChange: false});
      this.handleChange(ev);
    }
    this.params.onBlur?.(ev);
  }

  handleClearInput(ev: Event) {
    ev.stopPropagation();
    this.changeValue(``);
    this.params.onClear?.(ev);
    // without this, we focus on mp-input instead of just the input field. focusing on
    // mp-input prevents us from typing our second query, while focusing on the searchInput
    // allows to type something else.
    this.focus();
  }

  handleClick(ev: InputPointerEvent): void {
    this.params.onClick?.(ev);
  }

  handleSelectOnFocus() {
    if (this.params.selectOnFocus) {
      const alreadyHasFocus = document.activeElement === this;
      const selectionLength = this.searchInput?.selectionEnd - this.searchInput?.selectionStart;
      if (!alreadyHasFocus && !selectionLength) {
        // Don't select on focus if we already have focus or a selection because this will
        // make it so the current text cursor position or selection is overriden.
        this.select();
      }
    }
  }

  handleFocus(ev: InputFocusEvent): void {
    this.handleSelectOnFocus();
    this.params.onFocus?.(ev);
  }

  private parseCommifiedNumber(value: string): string {
    // Don't default an empty input to 0.
    if (value === ``) {
      return ``;
    }
    return Number(value.replace(/,/g, ``)).toString();
  }

  changeValue(value: string): string {
    this.updateInputWidth(value);
    let updatedValue = value;
    if (this.params.type === InputType.Number) {
      // Remove commas so we're always storing a raw number with the input.
      updatedValue = this.parseCommifiedNumber(value);
    }
    this.update({value: updatedValue});
    return updatedValue;
  }

  get displayValue(): string {
    if (this.params.type === InputType.Number) {
      // Don't default an empty input to 0.
      if (this.value === ``) {
        return ``;
      }
      const parsedNumber = Number(this.value);
      // Default behavior for number inputs is to not allow non-numeric values.
      if (isNaN(parsedNumber)) {
        return ``;
      }
      return commaizeNumber(parsedNumber);
    }
    return this.value;
  }

  get selectionStart() {
    return this.searchInput?.selectionStart;
  }

  scrollToStart(): void {
    if (this.inputWrapperEl) {
      this.inputWrapperEl.scrollLeft = 0;
    }
  }

  get value() {
    return this.state.value;
  }

  set value(value: string) {
    this.updateInputWidth(value);
    this.update({value});
  }

  /**
   * For auto resizing inputs, makes sure that the scrolling area is
   * shifted to the right as the user types or content grows.
   *
   * This behavior mimics the native input tag with overflowing content.
   */
  updateInputWidth(value: string = this.state.value) {
    if (!this.params.multilinePrefix) {
      return;
    }

    value ||= this.params.placeholder ?? null;
    if (value) {
      const currentTextWidth = this.state.textWidth ?? 0;

      const fontSizeMap = {
        [InputSize.Large]: mpFonts.fontSizeLarge,
        [InputSize.Medium]: mpFonts.fontSizeMedium,
        [InputSize.Small]: mpFonts.fontSizeSmall,
      };

      const textWidth =
        MeasureUtil.measureTextWidth(value, `400 ${fontSizeMap[this.params.size]} ${mpFonts.fontFamilyPrimary}`) + 16;

      this.update({textWidth});
      if ((this.searchInput && textWidth > currentTextWidth) ?? 0) {
        rafRetry(() => {
          this.searchInput.parentElement.scrollLeft = this.searchInput.parentElement.scrollWidth;
          return true;
        });
      }
    } else {
      this.update({textWidth: null});
    }
  }

  async focus() {
    await rafRetry(() => {
      this.searchInput?.focus();
      this.searchInput.parentElement.scrollLeft = this.searchInput.parentElement.scrollWidth;
      return Boolean(this.searchInput);
    });
  }

  blur() {
    this.searchInput?.blur();
    return super.blur();
  }

  select() {
    return this.searchInput?.select();
  }

  handleInputWrapperInsert(elm: HTMLElement): void {
    this.inputWrapperEl = elm;
  }

  // rename prefix wrapper
  handleInputPrefixInsert(elm: HTMLElement): void {
    this.inputPrefixEl = elm;
    // don't think this is needed because we should just enter this codepath once? but just in case
    this.prefixResizeObserver?.disconnect();
    this.prefixResizeObserver = new ResizeObserver(() => {
      const visibleInputWidth =
        this.inputWrapperEl.getBoundingClientRect().right - this.searchInput.getBoundingClientRect().left;
      // add a buffer to visible input width to account for off by one type issues
      const bufferPixels = 5;
      if (visibleInputWidth + bufferPixels < this.params.minInputWidth) {
        this.inputWrapperEl.scrollLeft += this.params.minInputWidth - visibleInputWidth;
      }
    });
    this.prefixResizeObserver.observe(this.inputPrefixEl);
  }

  handleSearchInputInsert(elm: HTMLInputElement) {
    this.searchInput = elm;
  }
}

// Temporary to contend with our React types being jacked up. Will be deleted
// when this gets converted to React "soon".
type EventTypes =
  | `onBlur`
  | `onChange`
  | `onClear`
  | `onClick`
  | `onFocus`
  | `onInput`
  | `onKeydown`
  | `onKeyup`
  | `onPaste`;
type FixedReactProps = Omit<ToReactProps<InputJsxProps>, EventTypes> & Pick<InputParams, EventTypes>;

export const InputEl = defineAndMakeJsxFactory<InputJsxProps>(`mp-input`, withStylesPanel(InputComponent, css));
export const InputReact = wc2react<FixedReactProps>(`mp-input`, withStylesPanel(InputComponent, css));
